/**
 * AMQ Finder — gemeinsame Quiz-Engine für Matratzen- und Topperfinder.
 *
 * Herausgelöst aus `amq-matratzenfinder.js`, das die Portierung des WordPress-Plugins war.
 * Alles, was beide Finder gleich machen, steht hier: Slides aufbauen, navigieren,
 * Fortschrittsleiste, Höhen nachführen, Eingaben prüfen, Ladescreen, Produkt und Variante
 * aus Shopify holen, Ergebnis anzeigen, Warenkorb, letztes Ergebnis merken.
 *
 * Was ein Finder eigenes mitbringt, steht in seinem „Rezept" (siehe `starten`): die
 * Schrittdefinition, der r-Code, die Ergebniskarte und einzelne Sonderregeln. Der
 * Matratzenfinder bringt darüber auch seinen Partnerlauf ein — die Engine weiß nichts
 * davon, sie kennt nur Schritte, die sich neu bauen lassen.
 *
 * Das erzeugte DOM ist unverändert das des Plugins — gleiche IDs, gleiche Klassen, bis hin
 * zu `swiper-slide-active` und `#matratenfinder-container` samt Tippfehler. Nur so gelten
 * die Stylesheets weiter. Belegt ist das durch einen Vorher/Nachher-Vergleich des DOM über
 * alle Schritte beider Läufe.
 */
(() => {
  'use strict';

  /* ── Bausteine, die beide Finder brauchen ───────────────────────────────── */

  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (z) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[z]);

  /**
   * Cent-Beträge im Format des Shops. Das Muster ist `{{ 123456 | money }}` aus der Section
   * (etwa „CHF 1,234.56"), also in der Währung des Besuchers — in der auch `/products/….js`
   * die Preise liefert. Ohne Muster bleibt es beim Euro-Format von früher.
   */
  function geldFormatierer(muster) {
    const m = /^(.*?)1(\D?)234(\D)56(.*)$/.exec(muster || '');
    if (!m) return (cent) => (cent / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
    const [, vor, tausend, dezimal, nach] = m;
    return (cent) => {
      const [ganz, rest] = (cent / 100).toFixed(2).split('.');
      return vor + ganz.replace(/\B(?=(\d{3})+(?!\d))/g, tausend) + dezimal + rest + nach;
    };
  }

  const HAKEN_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true"><path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/></svg>';
  const KREUZ_SVG =
    '<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 384 512"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg>';
  const PFEIL_SVG =
    '<svg class="arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" aria-hidden="true"><path d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"/></svg>';
  const INFO_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" aria-hidden="true"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></svg>';
  const CHEVRON_LINKS_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" aria-hidden="true"><path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"/></svg>';
  const CHEVRON_RECHTS_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" aria-hidden="true"><path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"/></svg>';
  const CHEVRON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="12" viewBox="0 0 24 12" fill="none" aria-hidden="true"><path d="M10.7897 11.5606C11.4591 12.1465 12.5462 12.1465 13.2157 11.5606L23.4979 2.56239C24.1674 1.97657 24.1674 1.02519 23.4979 0.439371C22.8285 -0.146452 21.7414 -0.146452 21.072 0.439371L12 8.37844L2.92804 0.444054C2.25862 -0.141769 1.17148 -0.141769 0.502064 0.444053C-0.167355 1.02988 -0.167355 1.98125 0.502064 2.56708L10.7843 11.5653L10.7897 11.5606Z" fill="#C2CDDA"/></svg>';
  const HAKEN_GRUEN_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true"><path d="M1 5L5 9L13 1" stroke="#5E9602" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const PUNKT_GRUEN_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><circle cx="5" cy="5" r="5" fill="#5E9602"/></svg>';
  const STERN_RAHMEN_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#FFBC00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z"/></svg>';
  const STERN_VOLL_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FFBC00" aria-hidden="true"><path d="M8.243 7.34l-6.38 .925l-.113 .023a1 1 0 0 0 -.44 1.684l4.622 4.499l-1.09 6.355l-.013 .11a1 1 0 0 0 1.464 .944l5.706 -3l5.693 3l.1 .046a1 1 0 0 0 1.352 -1.1l-1.091 -6.355l4.624 -4.5l.078 -.085a1 1 0 0 0 -.633 -1.62l-6.38 -.926l-2.852 -5.78a1 1 0 0 0 -1.794 0l-2.853 5.78z"/></svg>';

  /**
   * Fünf Sterne: einmal als Umriss, darüber dieselben gefüllt und auf den Anteil
   * beschnitten. Genau so löst es auch das Bewertungs-Plugin der alten Seite.
   */
  function sterneHtml(bewertung, maximum) {
    const max = maximum || 5;
    const anteil = Math.max(0, Math.min(1, bewertung / max)) * 100;
    const reihe = (svg) => svg.repeat(5);
    return `
<span class="mf-result__stars" role="img" aria-label="Bewertet mit ${esc(bewertung.toLocaleString('de-DE'))} von ${max}">
  <span class="mf-result__stars-base">${reihe(STERN_RAHMEN_SVG)}</span>
  <span class="mf-result__stars-front" style="width:${anteil.toFixed(1)}%">${reihe(STERN_VOLL_SVG)}</span>
</span>`;
  }

  /**
   * Der Ladescreen läuft mindestens so lange, auch wenn die Antwort schon da ist. Die API
   * braucht rund 200 ms — ohne diese Bremse blitzt der Spinner nur kurz auf, und das
   * Ergebnis wirkt wie ein fertiger Zettel statt wie eine Auswertung.
   *
   * Zwischen 2 und 3 Sekunden ist der brauchbare Bereich: darunter merkt es niemand,
   * darüber wird das Warten spürbar. Hier zu ändern, nicht an mehreren Stellen.
   */
  const LADESCREEN_MS = 2400;
  const LADE_TEXTE = [
    'Ihre Angaben werden ausgewertet …',
    'Größe, Gewicht und Schlafposition werden abgeglichen …',
    'Ihre Empfehlung wird zusammengestellt …',
  ];

  /* ── Fehlerberichte ─────────────────────────────────────────────────────────
     Ein kaputter Produkt-Handle sieht für den Besucher aus wie eine Störung und
     hinterlässt sonst nur eine Konsolenzeile auf seinem Gerät. Deshalb bekommt jeder
     Vorfall eine kurze Referenz, die auf dem Fehlerscreen steht, und wird an die
     Finder-API gemeldet — im Automation-Tool steht danach unter derselben Referenz,
     was genau schiefging.
     ──────────────────────────────────────────────────────────────────────── */

  /** Ohne I, O, 0 und 1 — die Referenz wird am Telefon vorgelesen und abgetippt. */
  const REFERENZ_ZEICHEN = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

  function referenzErzeugen(kuerzel) {
    const zufall = crypto.getRandomValues(new Uint8Array(8));
    const zeichen = Array.from(zufall, (b) => REFERENZ_ZEICHEN[b % REFERENZ_ZEICHEN.length]).join('');
    return `${kuerzel}-${zeichen.slice(0, 4)}-${zeichen.slice(4)}`;
  }

  /**
   * Hängt an einen Fehler an, was ihn einordbar macht. Ein `Error` reist durch mehrere
   * `await`-Ebenen nach oben — die Stelle, die ihn auffängt, weiß sonst nicht mehr, ob es
   * die API war oder ein Produkt, und schon gar nicht, welches.
   */
  function mitDetails(err, details) {
    return Object.assign(err, { amqDetails: details });
  }

  /** `/mf/v1/empfehlung` → `/mf/v1/fehler`; `null`, wenn die API-URL unbrauchbar ist. */
  function berichtUrl(apiUrl) {
    try {
      const url = new URL(apiUrl, location.origin);
      url.pathname = url.pathname.replace(/[^/]*$/, 'fehler');
      url.search = '';
      return url.href;
    } catch {
      return null;
    }
  }

  /* ══════════════════════════════════════════════════════════════════════════
     Ein Finder
     ══════════════════════════════════════════════════════════════════════════ */

  function starten(rezept) {
    const wurzel = document.querySelector(`[${rezept.wurzelAttribut}]`);
    const konfigEl = wurzel?.querySelector(`script[${rezept.konfigAttribut}]`);
    if (!wurzel || !konfigEl) return;

    let KONFIG;
    try {
      KONFIG = JSON.parse(konfigEl.textContent);
    } catch (err) {
      console.error(`[${rezept.name}] Konfiguration unlesbar`, err);
      return;
    }

    const icon = (name) => KONFIG.icons?.[name] ?? '';
    const geld = geldFormatierer(KONFIG.geldMuster);

    /*
     * Kiosk-Modus für die Tablets in den Showrooms (`kiosk` in der Section). Wie im Plugin:
     * nichts im Browser merken, kein Banner „letztes Ergebnis", kein ?r= in der Adresse —
     * sonst sähe der nächste Besucher das Ergebnis des vorigen. Statt Teilen gibt es „Ergebnis
     * per E-Mail erhalten". Welcher Showroom, steht als ?showroom=… in der Adresse; nur Werte
     * aus der Section-Einstellung zählen.
     */
    const kiosk = KONFIG.kiosk === true;
    const showroom = (() => {
      if (!kiosk) return null;
      const wert = (new URLSearchParams(window.location.search).get('showroom') || '').trim().toLowerCase();
      return (KONFIG.showrooms ?? []).includes(wert) ? wert : null;
    })();

    const container = wurzel.querySelector(`#${rezept.containerId}`);
    const finder = wurzel.querySelector(`#${rezept.finderId}`);
    const fortschritt = wurzel.querySelector(`#${rezept.fortschrittId}`);
    const pagination = fortschritt.querySelector('.swiper-pagination');
    const mobileLeiste = fortschritt.querySelector('.toggleNavigationMobile');
    if (!container || !finder || !fortschritt) return;

    let schritte = [];
    let index = 0;
    /** Antworten überleben einen Neuaufbau (beim Matratzenfinder den Wechsel auf Partner). */
    const antworten = new Map();
    let ergebnisAktiv = false;
    let hoehenBeobachter = null;

    /* ── Was das Rezept an die Hand bekommt ────────────────────────────────── */

    const gewaehlteWerte = (name) =>
      Array.from(container.querySelectorAll(`input[name="${name}"]:checked`)).map((e) => e.value);
    const wert = (name) => container.querySelector(`[name="${name}"]`)?.value ?? '';
    const werte = (name) => gewaehlteWerte(name).join(',');
    const gewaehlt = (name) => container.querySelector(`input[name="${name}"]:checked`)?.value ?? '';

    const kachel = (schritt, option) => {
      // `typ` beschreibt den Schritt, nicht das Feld: „koerper" mischt Zahlenfelder mit
      // Kacheln. Kacheln sind Radios — außer bei den Besonderheiten, wo mehrere gelten.
      const feldTyp = schritt.typ === 'checkbox' ? 'checkbox' : 'radio';
      // `vorausgewaehlt` hakt die Kachel schon beim Aufbau an, damit man ohne Klick weiterkommt.
      const eingabe = `<input type="${feldTyp}" name="${schritt.feld}" value="${option.wert}" data-shortinfo="${esc(option.info)}"${option.vorausgewaehlt ? ' checked' : ''}>`;
      const bild = option.bild
        ? `<picture decoding="async"><img src="${icon(option.bild)}" alt="${esc(option.text)}" width="150" height="150" loading="lazy"></picture>`
        : '';
      const beschreibung = schritt.nurText
        ? `<div class="description mf-radio-option-text"><span>${esc(option.text)}</span></div>`
        : `<div class="description"><span>${esc(option.text)}</span>${option.zusatz ? `<p>${esc(option.zusatz)}</p>` : ''}</div>`;
      return `<label>${eingabe}${bild}${beschreibung}</label>`;
    };

    async function produktHolen(handle) {
      let antwort;
      try {
        antwort = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
      } catch (err) {
        throw mitDetails(new Error(`Produkt ${handle} nicht erreichbar: ${err.message}`), { phase: 'produkt', handle });
      }
      if (!antwort.ok) {
        throw mitDetails(new Error(`Produkt ${handle}: HTTP ${antwort.status}`), {
          phase: 'produkt',
          handle,
          status: antwort.status,
          // 404 heißt hier praktisch immer: Handle im Theme zeigt auf ein umbenanntes Produkt.
          hinweis: antwort.status === 404 ? 'Handle existiert im Shop nicht' : undefined,
        });
      }
      return antwort.json();
    }

    /**
     * Lieferzeit einer Variante, derselbe Text wie auf der Produktseite („Lieferzeit 12–16
     * Werktage"). Die Bearbeitungszeit hängt als Metaobjekt an der Variante, und `/products/….js`
     * liefert keine Metafelder — deshalb rendert die Section amq-finder-lieferzeit den Text für
     * genau diese Variante. Wirft nie: Scheitert die Anfrage, fehlt nur die Zeile im Ergebnis.
     */
    async function lieferzeitHolen(handle, varianteId) {
      if (!varianteId) return '';
      const url = new URL(KONFIG.produkte?.[handle]?.url || `/products/${handle}`, location.origin);
      url.searchParams.set('variant', varianteId);
      url.searchParams.set('section_id', 'amq-finder-lieferzeit');
      try {
        const antwort = await fetch(url);
        if (!antwort.ok) {
          throw mitDetails(new Error(`Lieferzeit ${handle}: HTTP ${antwort.status}`), {
            phase: 'lieferzeit',
            handle,
            status: antwort.status,
          });
        }
        return new DOMParser().parseFromString(await antwort.text(), 'text/html').body.textContent.trim();
      } catch (err) {
        problemMelden({ art: 'warnung', fehler: err, phase: 'lieferzeit', handle });
        return '';
      }
    }

    /**
     * Findet die Variante über die Optionswerte, die die API mitliefert.
     *
     * Gelesen wird `v.options[i]`, nicht `v.option1…3`: Mehrere Produkte liegen in Shopify
     * als „Combined Listing" und haben deshalb eine vierte Option. Die Ajax-API führt nur
     * `option1` bis `option3` einzeln auf — der gesuchte Wert stünde dort in keinem Feld
     * und es käme nie eine Variante, also auch kein Preis zustande.
     *
     * Optionen, zu denen die API nichts sagt, bleiben unberücksichtigt: Die zusätzliche
     * Option eines Combined Listings hat pro Produkt ohnehin nur einen Wert.
     *
     * Härtegrade werden in Kurzform verglichen: Die Partnermatratzen heißen im Shop seit
     * 08.10.2026 „H2 & H4" statt „Härtegrade 2 & 4" — die API darf beide Schreibweisen liefern.
     */
    function optionswertNormalisieren(wert) {
      return String(wert)
        .replace(/^Härtegrade?\s+(\d)\s*&\s*(\d)$/, 'H$1 & H$2')
        .replace(/^Härtegrad\s+(\d)$/, 'H$1');
    }

    function varianteFinden(produkt, optionen) {
      const gesucht = new Map(optionen.map((o) => [o.name, optionswertNormalisieren(o.value)]));
      return produkt.variants.find((v) =>
        produkt.options.every((name, i) => {
          const soll = gesucht.get(typeof name === 'string' ? name : name.name);
          return soll === undefined || optionswertNormalisieren(v.options[i]) === soll;
        }),
      );
    }

    /**
     * Meldet einen Vorfall an die Finder-API und gibt die Referenz zurück, die der Besucher
     * zu sehen bekommt. Bewusst ohne `await` beim Aufrufer und mit verschlucktem Fehler: ein
     * misslungener Bericht darf die Anzeige nicht zusätzlich aufhalten oder kaputtmachen.
     *
     * `art: 'warnung'` ist für Defekte, die der Besucher nicht merkt — etwa eine Variante, die
     * sich nicht auflösen lässt: die Karte steht dann ohne Preis da, sieht aber heil aus.
     */
    function problemMelden({ art = 'fehler', fehler: err, phase, meldung, handle, status, code }) {
      const details = err?.amqDetails ?? {};
      const referenz = referenzErzeugen(rezept.kuerzel);
      const bericht = {
        referenz,
        finder: rezept.name.toLowerCase(),
        art,
        phase: phase ?? details.phase ?? 'unbekannt',
        meldung: meldung ?? err?.message ?? 'Unbekannter Fehler',
        handle: handle ?? details.handle,
        hinweis: details.hinweis,
        status: status ?? details.status,
        code,
        seite: location.pathname + location.search,
      };
      console.error(`[${rezept.name}] ${referenz}`, bericht, err ?? '');
      const url = berichtUrl(KONFIG.apiUrl);
      if (url) {
        // `keepalive`, damit der Bericht auch noch rausgeht, wenn der Besucher die Seite
        // im selben Moment verlässt — genau dann ist der Fehler am ärgerlichsten.
        fetch(url, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bericht),
        }).catch(() => {});
      }
      return referenz;
    }

    const hilfen = {
      esc, geld, icon, kachel, sterneHtml,
      HAKEN_SVG, KREUZ_SVG, PFEIL_SVG, INFO_SVG, CHEVRON_LINKS_SVG, CHEVRON_RECHTS_SVG, CHEVRON_SVG,
      HAKEN_GRUEN_SVG, PUNKT_GRUEN_SVG,
      produktHolen, varianteFinden, lieferzeitHolen, problemMelden, mitDetails,
      wert, werte, gewaehlt, gewaehlteWerte,
      get KONFIG() { return KONFIG; },
      kiosk,
      showroom,
      get container() { return container; },
      get wurzel() { return wurzel; },
      get index() { return index; },
      get schritte() { return schritte; },
      neuAufbauen: () => quizAufbauen(),
      meldungZeigen: (html) => meldungZeigen(html),
      zeigeSchritt: (i) => zeigeSchritt(i),
      antworten,
    };

    /* ── Slides ────────────────────────────────────────────────────────────── */

    function slideHtml(schritt) {
      const eigen = rezept.slideHtml?.(schritt, hilfen);
      if (typeof eigen === 'string') return eigen;

      const kopf = schritt.untertitel ? `<div class="mf-sub-header"><h3>${schritt.untertitel}</h3></div>` : '';
      return `
<div class="swiper-slide ${schritt.klasse}" data-hash="${schritt.hash}" data-shortname="${esc(schritt.kurz)}" data-video="${schritt.video}">
  ${kopf}
  <div class="mf-body">
    <h2>${esc(schritt.frage)}</h2>
    ${schritt.hinweis ? `<p class="mf-subheader">${esc(schritt.hinweis)}</p>` : ''}
    <div class="flex-container">${schritt.optionen.map((o) => kachel(schritt, o)).join('')}</div>
  </div>
</div>`;
    }

    /* ── Aufbau und Navigation ─────────────────────────────────────────────── */

    function quizAufbauen() {
      schritte = rezept.schritteBauen(hilfen);
      container.innerHTML = `
<form class="hg-form hg-calc" id="hg-calc-form" novalidate>
  <div class="swiper mySwiper">
    <div class="swiper-wrapper">${schritte.map(slideHtml).join('')}</div>
  </div>
</form>
<div class="mf-navigation">
  <button type="button" class="button-secondary button-prev-step disabled" aria-label="Schritt zurück">
    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/></svg>
    <span>Zurück</span>
  </button>
  <button type="button" class="button-primary button-next-step" aria-label="Nächster Schritt">
    <span>Weiter</span>
    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z"/></svg>
  </button>
  <a class="button-secondary button-restart" href="${esc(neustartUrl())}">${rezept.neustartText}</a>
</div>
<dialog id="dialog_eingabe_error">
  <button type="button" class="button-close button-close-x" aria-label="Fenster schließen">${KREUZ_SVG}</button>
  <div class="mf-dialog-symbol" aria-hidden="true">${INFO_SVG}</div>
  <div class="content content-flex"></div>
</dialog>`;

      antwortenEinsetzen();
      if (!kiosk) letztesErgebnisBanner();
      verdrahten();
      fortschrittZeichnen();
      hoeheBeobachten();
      zeigeSchritt(Math.min(index, schritte.length - 1), false);
    }

    /** Im Kiosk startet der Finder neu, ohne den Showroom aus der Adresse zu verlieren. */
    function neustartUrl() {
      if (!kiosk) return KONFIG.seitenUrl || '';
      const url = new URL(window.location.href);
      url.searchParams.delete('r');
      url.hash = '';
      return url.toString();
    }

    const wrapper = () => container.querySelector('.swiper-wrapper');
    const slides = () => Array.from(container.querySelectorAll('.swiper-slide'));

    /**
     * Swipers `autoHeight`: Alle Schritte liegen nebeneinander und sind unterschiedlich
     * hoch. Ohne eine gesetzte Höhe wäre der Rahmen immer so hoch wie der längste Schritt
     * — unter den kurzen stünde dann eine große Leerfläche.
     */
    function hoeheAnpassen() {
      const w = wrapper();
      const slide = slides()[index];
      if (w && slide) w.style.height = `${slide.offsetHeight}px`;
    }

    /**
     * Bilder und Schriften kommen nach, Felder klappen auf, das Fenster ändert sich:
     * Jedes Mal ändert sich die Höhe des Schritts. Ein ResizeObserver hält sie nach.
     */
    function hoeheBeobachten() {
      hoehenBeobachter?.disconnect();
      if (typeof ResizeObserver !== 'function') return;
      hoehenBeobachter = new ResizeObserver(() => hoeheAnpassen());
      slides().forEach((s) => hoehenBeobachter.observe(s));
    }

    function zeigeSchritt(neu, animiert = true) {
      index = Math.max(0, Math.min(neu, schritte.length - 1));
      const alle = slides();
      alle.forEach((s, i) => {
        s.classList.toggle('swiper-slide-active', i === index);
        s.classList.toggle('swiper-slide-prev', i === index - 1);
        s.classList.toggle('swiper-slide-next', i === index + 1);
        s.setAttribute('aria-label', `${i} / ${alle.length - 1}`);
        s.toggleAttribute('inert', i !== index);
      });
      const w = wrapper();
      w.style.transition = animiert ? 'transform 0.3s ease' : 'none';
      w.style.transform = `translate3d(-${index * 100}%, 0, 0)`;

      // Der erste Aufruf ist der Aufbau, jeder weitere ein echter Schrittwechsel.
      if (animiert) hakenErlaubt = true;

      container.querySelector('.button-prev-step').classList.toggle('disabled', index === 0);
      hoeheAnpassen();
      fortschrittAktualisieren();
      rezept.beiSchrittwechsel?.(hilfen);
      videoSchrittSetzen();
      untertitelUmschalten();
    }

    /** Der Hero-Untertitel steht nur auf dem ersten Schritt. */
    function untertitelUmschalten() {
      const untertitel = wurzel.querySelector('.am6m-subtitle');
      if (untertitel) untertitel.style.display = index >= 1 ? 'none' : '';
    }

    /** Ist der Schritt vollständig beantwortet? */
    function beantwortet(i) {
      const slide = slides()[i];
      if (!slide) return false;
      const felder = slide.querySelectorAll('input[type="number"]');
      for (const f of felder) {
        const w = Number(f.value);
        if (!f.value || Number.isNaN(w) || w < Number(f.min) || w > Number(f.max)) return false;
      }
      const gruppen = new Set(
        Array.from(slide.querySelectorAll('input[type="radio"], input[type="checkbox"]')).map((e) => e.name),
      );
      for (const name of gruppen) {
        if (!slide.querySelector(`input[name="${name}"]:checked`)) return false;
      }
      return true;
    }

    /**
     * Was bei jedem Neuaufbau neu verdrahtet werden muss: Die Knöpfe und das Formular
     * werden jedes Mal neu erzeugt, ihre alten Knoten samt Zuhörern verschwinden mit.
     */
    function verdrahten() {
      container.querySelector('.button-next-step').addEventListener('click', weiter);
      container.querySelector('.button-prev-step').addEventListener('click', () => {
        if (index > 0) zeigeSchritt(index - 1);
      });
      container.querySelector('#hg-calc-form').addEventListener('submit', (e) => e.preventDefault());
      rezept.nachAufbau?.(hilfen);
    }

    /**
     * Was genau einmal verdrahtet wird: Diese Zuhörer hängen am Container, und der bleibt
     * beim Neuaufbau bestehen — nur sein Inhalt wird ersetzt. Würden sie in `verdrahten`
     * stehen, käme bei jedem Neuaufbau ein zweiter Satz dazu und jeder Klick zählte doppelt.
     */
    function delegationVerdrahten() {
      container.addEventListener('change', (e) => {
        const ziel = e.target;
        if (!(ziel instanceof HTMLInputElement) && !(ziel instanceof HTMLSelectElement)) return;
        antworten.set(ziel.name, ziel.type === 'checkbox' ? gewaehlteWerte(ziel.name) : ziel.value);

        // Das Rezept darf hier neu aufbauen (Matratzenfinder: Wechsel auf den Partnerlauf).
        // Meldet es `true`, ist der Container ersetzt und alles Weitere hinfällig.
        if (rezept.beiAenderung?.(ziel, hilfen) === true) return;

        // „Auf mich trifft nichts davon zu" schließt die anderen Angaben aus.
        if (ziel.type === 'checkbox' && istAusschlussGruppe(ziel.name)) {
          const gruppe = Array.from(container.querySelectorAll(`input[name="${ziel.name}"]`));
          if (ziel.value === 'N' && ziel.checked) {
            gruppe.forEach((e2) => { if (e2.value !== 'N') e2.checked = false; });
          } else if (ziel.checked) {
            const keine = gruppe.find((e2) => e2.value === 'N');
            if (keine) keine.checked = false;
          }
          // Das Plugin des Topperfinders hakt „keiner der Punkte" wieder an, sobald gar
          // nichts mehr gewählt ist. Der Matratzenfinder kennt das nicht.
          if (rezept.ausschlussFaelltZurueck && !gruppe.some((e2) => e2.checked)) {
            const keine = gruppe.find((e2) => e2.value === 'N');
            if (keine) keine.checked = true;
          }
          antworten.set(ziel.name, gewaehlteWerte(ziel.name));
        }
        kurzinfoAktualisieren();
      });

      // Bewusste Abweichung vom Plugin: Dort schob eine Auswahl per Klick nach 180 ms von
      // selbst zum nächsten Schritt. Hier geht es erst auf „Weiter“ weiter — eine
      // Korrektur lässt sich sonst nur über „Zurück“ erreichen.

      // Jeder Knopf mit `button-close` schließt den Dialog; mit `slide-next` geht es weiter.
      container.addEventListener('click', (e) => {
        const knopf = e.target instanceof Element ? e.target.closest('dialog .button-close') : null;
        if (!knopf) return;
        e.preventDefault();
        container.querySelector('dialog[open]')?.close();
        if (knopf.classList.contains('slide-next')) {
          if (rezept.beiSchrittAbschluss) weiter();
          else zeigeSchritt(index + 1);
        }
      });
    }

    const istAusschlussGruppe = (name) =>
      rezept.istAusschlussGruppe ? rezept.istAusschlussGruppe(name) : name.endsWith('besonderheiten');

    function antwortenEinsetzen() {
      for (const [name, w] of antworten) {
        container.querySelectorAll(`[name="${name}"]`).forEach((f) => {
          if (f.type === 'radio') f.checked = f.value === w;
          else if (f.type === 'checkbox') f.checked = Array.isArray(w) && w.includes(f.value);
          else f.value = w;
        });
      }
    }

    function weiter() {
      const meldung = pruefung(index);
      if (meldung) {
        meldungZeigen(meldung);
        return;
      }
      if (index < schritte.length - 1) {
        rezept.beiSchrittAbschluss?.(schritte[index], index, hilfen);
        zeigeSchritt(index + 1);
        return;
      }
      // Letzter Schritt: Das Rezept darf den Abschluss noch verhindern — der Topperfinder
      // führt hier seine Sperre aus, wenn die vorhandene Matratze zu stark verschlissen ist.
      const sperre = rezept.vorErgebnis?.(hilfen);
      if (sperre) {
        meldungZeigen(sperre);
        return;
      }
      rezept.beiSchrittAbschluss?.(schritte[index], index, hilfen);
      ergebnisHolen();
    }

    /**
     * Prüft den Schritt und gibt die Meldung zurück, oder `null`, wenn es weitergehen darf.
     * Erst die Sonderprüfungen des Rezepts, dann die allgemeine Frage, ob überhaupt etwas
     * ausgewählt wurde — Reihenfolge wie im Original.
     */
    function pruefung(i) {
      const schritt = schritte[i];
      const slide = slides()[i];
      if (!schritt || !slide) return null;
      const eigen = rezept.pruefung?.(schritt, slide, hilfen);
      if (eigen) return eigen;
      if (!beantwortet(i)) return '<p>Bitte wählen Sie aus, was am ehesten zu Ihnen passt.</p>';
      return null;
    }

    /** Entspricht `showErrorMessage` im Plugin: Text einsetzen, Dialog öffnen. */
    function meldungZeigen(html) {
      const d = container.querySelector('#dialog_eingabe_error');
      if (!d) return;
      d.querySelector('.content').innerHTML = html;
      d.showModal();
    }

    /* ── Fortschrittsleiste ────────────────────────────────────────────────── */

    const abschnittKopf = (klasse, kopfKlasse, text, navKlasse) => `
<div class="${klasse}">
  <a class="progressbarElement expand_data ${kopfKlasse}">
    <div class="check">${HAKEN_SVG}</div>
    <div class="bullet-text"><div class="sub-nav-heading">${esc(text)} ${PFEIL_SVG}</div></div>
  </a>
  <div class="sub-navigation ${navKlasse}">`;

    function fortschrittZeichnen() {
      let html = '';
      schritte.forEach((schritt, i) => {
        // Das Rezept kann vor einem Schritt einen Abschnitt aufmachen (Matratzenfinder:
        // „Meine Angaben" / „Angaben Partner/in").
        html += rezept.abschnittVor?.(schritt, abschnittKopf) ?? '';
        const name = rezept.kurznamen?.[schritt.kurz] ?? schritt.kurz;
        html += `
<a href="#${schritt.hash}" class="progressbarElement swiper-pagination-bullet" data-index="${i}">
  <div class="check">${HAKEN_SVG}</div>
  <div class="bullet-text">
    <div class="sub-heading">${esc(name)}</div>
    <div class="sub-text" id="short-info_${schritt.hash}">--</div>
  </div>
</a>`;
        const letzter = i === schritte.length - 1;
        const abschnittEnde = rezept.abschnittEndetNach?.(schritt) ?? false;
        if (abschnittEnde) html += '';
        else if (!letzter) html += '<div class="line-between"></div>';
        else html += rezept.abschnittAmEnde?.() ?? '';
      });

      pagination.innerHTML = html;
      rezept.nachFortschritt?.(pagination);
      pagination.insertAdjacentHTML('beforeend', `<div class="am6m-arrow-btn" role="button" tabindex="0" aria-label="Schritte anzeigen">${CHEVRON_SVG}</div>`);

      pagination.querySelectorAll('.progressbarElement[data-index]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          const ziel = Number(el.dataset.index);
          if (ziel < index || beantwortet(ziel)) zeigeSchritt(ziel);
        });
      });
      pagination.querySelector('.am6m-arrow-btn')?.addEventListener('click', () => fortschritt.classList.toggle('active'));
      pagination.querySelectorAll('.expand_data').forEach((el) => {
        el.addEventListener('click', () => el.parentElement?.classList.toggle('active'));
      });

      mobileLeiste.innerHTML = schritte
        .map((_, i) => `<div class="progressbarElement swiper-pagination-bullet" data-index="${i}"><div class="check">${HAKEN_SVG}</div></div>`)
        .join('<div class="line-between"></div>');
    }

    /**
     * `toggleNavigation` des Plugins: Pfeil drehen und die Bezeichnungen der Schritte
     * ein- oder ausblenden. Der Umschalter steht fest im Markup, nicht im Neuaufbau —
     * deshalb wird er genau einmal verdrahtet.
     *
     * Auf dem Telefon hängt daran die Klasse `mf-namen-offen`: Die Leiste wird dann von
     * einer Punktreihe zu einer Liste, in der jeder Schritt seinen Namen trägt. Die
     * alten Klassen (`hidden` auf beiden Leisten) werden weiter mitgeschaltet, damit das
     * Verhalten auf großen Bildschirmen unverändert bleibt.
     */
    function umschalterVerdrahten() {
      const knopf = fortschritt.querySelector('.toggle-nav');
      if (!knopf) return;
      const umschalten = () => {
        const zu = pagination.classList.toggle('hidden');
        knopf.classList.toggle('close');
        mobileLeiste.classList.toggle('hidden');
        fortschritt.classList.toggle('mf-namen-offen', !zu);
        knopf.setAttribute('aria-expanded', String(!zu));
      };
      knopf.addEventListener('click', umschalten);
      knopf.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          umschalten();
        }
      });
    }

    /**
     * Im Plugin füllt `fillValuesInShortInfo` die waagerechte Leiste erst beim ersten
     * Schrittwechsel. Beim Laden ist deshalb noch nichts abgehakt — auch der erste Schritt
     * nicht, obwohl Breite und Länge vorbelegt sind. Ohne diese Bremse leuchtet der erste
     * Verbindungsstrich sofort blau, was im Original nicht passiert. Die Punktreihe für
     * Mobilgeräte baut das Plugin an anderer Stelle und hakt dort von Anfang an ab.
     */
    let hakenErlaubt = false;

    function fortschrittAktualisieren() {
      const fertig = schritte.map((_, i) => beantwortet(i));
      [pagination, mobileLeiste].forEach((leiste) => {
        const haken = leiste === mobileLeiste || hakenErlaubt;
        leiste.querySelectorAll('.progressbarElement[data-index]').forEach((el) => {
          const i = Number(el.dataset.index);
          el.classList.toggle('correct', haken && fertig[i]);
          el.classList.toggle('swiper-pagination-bullet-active', i === index);
        });
      });

      kurzinfoAktualisieren();
      rezept.nachFortschrittAktualisierung?.(pagination, fertig, schritte);
    }

    /**
     * Die Kurzinfo darf sich sofort ändern — sie steht unter dem Schrittnamen und ist im
     * neuen Design ohnehin ausgeblendet. Die Haken und der blaue Verbindungsstrich gehören
     * dagegen in `fortschrittAktualisieren`, das nur beim Schrittwechsel läuft: Ein Klick
     * auf eine Kachel soll den Strich noch nicht füllen.
     */
    function kurzinfoAktualisieren() {
      schritte.forEach((schritt, i) => {
        const ziel = pagination.querySelector(`#short-info_${CSS.escape(schritt.hash)}`);
        if (!ziel) return;
        const slide = slides()[i];
        const infos = Array.from(slide?.querySelectorAll('input:checked') ?? [])
          .map((e) => e.dataset.shortinfo)
          .filter(Boolean);
        ziel.textContent = infos.length ? infos.join(', ') : '--';
      });
    }

    /* ── Videokarte ────────────────────────────────────────────────────────────
       Die Quellen werden erst beim Klick gesetzt. Vorher lädt nur das Cover — sieben
       Videos im Voraus wären rund 50 MB für etwas, das die meisten nie ansehen.
       Finder ohne Videos (Topperfinder) lassen `videoTexte` weg; dann passiert hier nichts.
       ──────────────────────────────────────────────────────────────────────── */

    const videoEl = wurzel.querySelector('#mf-videos');
    const videoCover = wurzel.querySelector('.am6m-video-cover');
    const videoText = wurzel.querySelector('.am6m-video-text-container');
    const videoUntertitel = wurzel.querySelector('.am6m-video-subtitle');
    const abspielKnopf = wurzel.querySelector('.am6m-video-play-btn');
    const videoTexte = rezept.videoTexte;

    function videoSchrittSetzen() {
      if (!videoTexte) return;
      if (videoUntertitel) videoUntertitel.textContent = videoTexte[index] ?? videoTexte[0];
      if (videoEl && !videoEl.paused) videoEl.pause();
      videoEl?.removeAttribute('src');
      videoCover?.classList.remove('am6m-cover-hidden');
      videoText?.classList.remove('am6m-video-text-hidden');
    }

    function videoAbspielen() {
      const schluessel = schritte[index]?.video ?? 'intro';
      const daten = KONFIG.videos?.[schluessel];
      if (!daten || !videoEl) return;
      // Mittlere Auflösung reicht für eine Karte von 192 px Breite.
      const quellen = [...(daten.sources ?? [])].sort((a, b) => (a.height || 0) - (b.height || 0));
      const quelle = quellen.find((q) => (q.height || 0) >= 480) ?? quellen[0];
      if (!quelle) return;
      videoEl.src = quelle.url;
      videoEl.dataset.trackingVideo = schluessel;
      videoEl.setAttribute('controls', '');
      videoCover?.classList.add('am6m-cover-hidden');
      videoText?.classList.add('am6m-video-text-hidden');
      videoEl.play().catch(() => {});
    }

    if (videoTexte) {
      abspielKnopf?.addEventListener('click', videoAbspielen);
      videoCover?.addEventListener('click', videoAbspielen);
      videoEl?.addEventListener('timeupdate', () => {
        if (videoEl.duration > 0) rezept.beiVideoFortschritt?.(videoEl.dataset.trackingVideo, Math.floor(videoEl.currentTime / videoEl.duration * 100));
      });
    }

    /* ── Ergebnis ──────────────────────────────────────────────────────────── */

    /** Dreht die Texte weiter und löst auf, sobald die Mindestzeit um ist. */
    function ladescreenStarten() {
      let i = 0;
      const takt = window.setInterval(() => {
        i += 1;
        const text = container.querySelector('.mf-ladetext');
        if (!text || !LADE_TEXTE[i]) return;
        // Kurz ausblenden, damit der nächste Satz nicht hart umspringt.
        text.style.opacity = '0';
        window.setTimeout(() => {
          text.textContent = LADE_TEXTE[i];
          text.style.opacity = '1';
        }, 200);
      }, Math.round(LADESCREEN_MS / LADE_TEXTE.length));
      return new Promise((fertig) => {
        window.setTimeout(() => {
          window.clearInterval(takt);
          fertig();
        }, LADESCREEN_MS);
      });
    }

    const ladeHtml = () => `
<div class="mf-body">
  <div class="mf-ladescreen">
    <div class="mf-ladespinner" aria-hidden="true"></div>
    <p class="mf-ladetext" role="status" aria-live="polite">${LADE_TEXTE[0]}</p>
  </div>
</div>`;

    const hinweisHtml = () => `
<div class="mf-body">
  <h2>${rezept.hinweisKopf}</h2>
  <p>${rezept.hinweisText}</p>
</div>`;

    const fehlerHtml = (referenz) => `
<div class="mf-body">
  <h2>Das hat leider nicht geklappt</h2>
  <p>Bitte versuchen Sie es in einem Moment noch einmal oder rufen Sie uns an.</p>
  <p class="mf-fehlercode">Fehlercode: <b>${esc(referenz)}</b></p>
</div>`;

    /**
     * Holt die Empfehlung und zeigt sie an. Die Engine besorgt die Antwort und kümmert
     * sich um Ladescreen und Fehlerfälle; das Rezept macht daraus die Karte — samt der
     * Produkte, die es dafür braucht.
     */
    async function ergebnisHolen(codeVorgabe) {
      const code = codeVorgabe ?? rezept.rCode(hilfen);
      ergebnisAnzeigen(ladeHtml());
      const ladescreen = codeVorgabe ? Promise.resolve() : ladescreenStarten();
      try {
        const antwort = await fetch(`${KONFIG.apiUrl}?r=${encodeURIComponent(code)}`);
        const daten = await antwort.json();
        if (!daten.ok) {
          throw mitDetails(new Error(daten.meldung || 'Empfehlung nicht möglich'), {
            phase: 'api',
            status: antwort.status,
            hinweis: daten.fehler,
          });
        }
        if (daten.art === 'keine') {
          await ladescreen;
          ergebnisAnzeigen(hinweisHtml());
          return;
        }
        const fertig = await rezept.ergebnisAufbereiten(daten, hilfen);
        await ladescreen;
        ergebnisAnzeigen(rezept.ergebnisHtml(daten, fertig, code, hilfen));
        // Nur ein selbst durchlaufenes Quiz überschreibt das gemerkte Ergebnis. Ein
        // geteilter Link tat das im Original auch nicht — sonst verdrängte fremdes
        // Ergebnis das eigene.
        if (!codeVorgabe && !kiosk) letztesErgebnisMerken(daten, fertig, code);
        // Für das Google-Tracking des Rezepts; `geteilt` heißt: per ?r=… geöffnet.
        rezept.beiErgebnis?.(daten, fertig, { geteilt: Boolean(codeVorgabe) }, hilfen);
      } catch (err) {
        const referenz = problemMelden({ fehler: err, code });
        await ladescreen;
        ergebnisAnzeigen(fehlerHtml(referenz));
      }
    }

    function ergebnisAnzeigen(html) {
      ergebnisAktiv = true;
      finder.classList.add('result-screen-active');
      wurzel.classList.add('has-result');
      const w = container.querySelector('.swiper-wrapper');
      w.innerHTML = `<div class="swiper-slide ergebnis swiper-slide-active" data-hash="ergebnis">${html}</div>`;
      // Der Wrapper steht vom letzten Schritt noch weit links (etwa `-700%`) und trägt
      // dessen Übergangsregel. Ohne dieses Abschalten würde der Rücksprung auf 0
      // mitanimiert — das Ergebnis führe dann über die ganze Breite von rechts herein,
      // was aussieht wie ein weiterer Schritt statt wie ein Ergebnis. Es soll
      // stattdessen an Ort und Stelle aufblenden (siehe `.swiper-slide.ergebnis` im
      // Stylesheet).
      w.style.transition = 'none';
      w.style.transform = 'translate3d(0,0,0)';
      // Der Ergebnis-Screen ist der einzige Schritt und wächst beim Nachladen von Bild
      // und Preis — hier ist eine feste Höhe nur im Weg.
      w.style.height = 'auto';
      hoehenBeobachter?.disconnect();
      container.querySelector('.button-next-step')?.classList.add('hidden');
      container.querySelector('.button-prev-step')?.classList.add('hidden');
      warenkorbVerdrahten();
      bilderstreifenVerdrahten();
      if (kiosk) ergebnisMailEinbauen();
      else ergebnisLinkSetzen();
    }

    /**
     * Der Bilderstreifen unter dem Ergebnis. Im Original schiebt ihn Swiper — mit der Maus
     * durch `simulateTouch`, Knöpfe blendet das Ergebnis-Stylesheet aus. Ohne die Bibliothek
     * ist der Streifen hier ein waagerechter Scrollbereich; ergänzt wird, was dabei fehlt:
     * Ziehen mit der Maus, zwei Pfeile, die um je eine Kachelbreite weiterrücken, und auf
     * dem Handy eine Punktreihe darunter.
     *
     * Gescrollt wird je nach Breite ein anderes Element: am Desktop der äußere Rahmen
     * (`.mf-result__slider-body`), auf dem Handy der innere Streifen (`.swiper`, siehe
     * „kleine Bildschirme" im Stylesheet). Welches es gerade ist, sagt sein `overflow-x`.
     */
    function bilderstreifenVerdrahten() {
      const streifen = container.querySelector('.mf-result__slider-body');
      if (!streifen) return;
      const innen = streifen.querySelector('.swiper');
      const zurueck = streifen.parentElement.querySelector('.mf-slider-pfeil--zurueck');
      const vor = streifen.parentElement.querySelector('.mf-slider-pfeil--vor');
      if (!zurueck || !vor) return;
      const punkte = [...streifen.closest('.mf-result__slider-section').querySelectorAll('.mf-slider-punkt')];

      const scroller = () => (innen && getComputedStyle(innen).overflowX !== 'visible' ? innen : streifen);

      const kachelbreite = () => {
        const erste = streifen.querySelector('.swiper-slide');
        const reihe = streifen.querySelector('.swiper-wrapper');
        const abstand = parseFloat(getComputedStyle(reihe).columnGap) || 0;
        return (erste?.getBoundingClientRect().width ?? 320) + abstand;
      };

      const aktualisieren = () => {
        const s = scroller();
        const rest = s.scrollWidth - s.clientWidth - s.scrollLeft;
        zurueck.hidden = s.scrollLeft <= 1;
        vor.hidden = rest <= 1;
        if (!punkte.length) return;
        // Am Ende reicht der Weg oft nicht für eine ganze Kachel — dann gilt das letzte Bild.
        const index = rest <= 1 ? punkte.length - 1 : Math.round(s.scrollLeft / kachelbreite());
        punkte.forEach((p, i) => p.setAttribute('aria-current', String(i === index)));
      };

      zurueck.addEventListener('click', () => scroller().scrollBy({ left: -kachelbreite(), behavior: 'smooth' }));
      vor.addEventListener('click', () => scroller().scrollBy({ left: kachelbreite(), behavior: 'smooth' }));
      punkte.forEach((p, i) =>
        p.addEventListener('click', () => scroller().scrollTo({ left: i * kachelbreite(), behavior: 'smooth' })),
      );
      // `scroll` steigt nicht auf — beide Kandidaten brauchen ihren eigenen Lauscher.
      streifen.addEventListener('scroll', aktualisieren, { passive: true });
      innen?.addEventListener('scroll', aktualisieren, { passive: true });
      window.addEventListener('resize', aktualisieren);
      // Die Bilder laden noch, wenn das Ergebnis erscheint — vorher stimmt die Breite nicht.
      window.setTimeout(aktualisieren, 300);
      aktualisieren();

      let startX = 0;
      let startLinks = 0;
      let zieht = false;
      streifen.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        zieht = true;
        startX = e.clientX;
        startLinks = streifen.scrollLeft;
        streifen.classList.add('mf-zieht');
        streifen.setPointerCapture(e.pointerId);
      });
      streifen.addEventListener('pointermove', (e) => {
        if (!zieht) return;
        e.preventDefault();
        streifen.scrollLeft = startLinks - (e.clientX - startX);
      });
      const loslassen = (e) => {
        if (!zieht) return;
        zieht = false;
        streifen.classList.remove('mf-zieht');
        if (streifen.hasPointerCapture(e.pointerId)) streifen.releasePointerCapture(e.pointerId);
        aktualisieren();
      };
      streifen.addEventListener('pointerup', loslassen);
      streifen.addEventListener('pointercancel', loslassen);
    }

    /**
     * `addRLinkToUrl` des Plugins: Der Code wandert in die Adresszeile, damit ein geteilter
     * oder gespeicherter Link direkt wieder auf dieselbe Empfehlung führt. `replaceState`
     * statt `pushState`, damit der Zurück-Knopf nicht erst durch das Ergebnis muss.
     */
    function ergebnisLinkSetzen() {
      const code = container.querySelector('[data-mf-code]')?.dataset.mfCode;
      if (!code) return;
      const url = new URL(window.location.href);
      url.searchParams.set('r', code);
      window.history.replaceState(null, '', url.toString());
    }

    /**
     * „Ergebnis per E-Mail erhalten" (Kiosk). Das Plugin schickte die Mail selbst; hier macht
     * das die Finder-API (`…/ergebnis-mail` neben dem Empfehlungs-Endpoint). Nach dem Senden
     * wird das Feld geleert, damit die Adresse nicht für den nächsten Besucher stehen bleibt.
     */
    function ergebnisMailEinbauen() {
      const ergebnis = container.querySelector('[data-mf-code]');
      const code = ergebnis?.dataset.mfCode;
      // Das angezeigte Produkt: Die Mail nennt dasselbe, auch wenn das Rezept einmal tauscht.
      const handle = ergebnis?.dataset.mfHandle || null;
      const ziel = container.querySelector('.mf-result__cta');
      if (!code || !ziel) return;
      let mailUrl;
      try {
        const url = new URL(KONFIG.apiUrl, window.location.origin);
        url.pathname = url.pathname.replace(/[^/]*$/, 'ergebnis-mail');
        url.search = '';
        mailUrl = url.href;
      } catch {
        return;
      }

      const block = document.createElement('div');
      block.className = 'mf-kiosk-mail';
      block.innerHTML = `
<button type="button" class="button-secondary mf-kiosk-mail__oeffnen" aria-expanded="false">Ergebnis per E-Mail erhalten</button>
<form class="mf-kiosk-mail__form" hidden novalidate>
  <label class="mf-kiosk-mail__label" for="mf-kiosk-mail-adresse">Ihre E-Mail-Adresse</label>
  <div class="mf-kiosk-mail__zeile">
    <input id="mf-kiosk-mail-adresse" type="email" name="email" autocomplete="off" spellcheck="false" autocapitalize="off" maxlength="254" required>
    <button type="submit" class="button-primary">Senden</button>
  </div>
  <input type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true" class="mf-kiosk-mail__hp">
  <p class="mf-kiosk-mail__hinweis">Wir schicken Ihnen nur diese eine E-Mail mit Ihrem Ergebnis, keinen Newsletter.</p>
</form>
<p class="mf-kiosk-mail__meldung" role="status" hidden></p>`;
      ziel.after(block);

      const oeffnen = block.querySelector('.mf-kiosk-mail__oeffnen');
      const form = block.querySelector('form');
      const feld = form.elements.email;
      const meldung = block.querySelector('.mf-kiosk-mail__meldung');
      const senden = form.querySelector('[type="submit"]');

      oeffnen.addEventListener('click', () => {
        form.hidden = false;
        oeffnen.hidden = true;
        oeffnen.setAttribute('aria-expanded', 'true');
        feld.focus();
      });

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        meldung.hidden = true;
        meldung.classList.remove('mf-kiosk-mail__meldung--fehler');
        const email = feld.value.trim();
        if (!feld.checkValidity() || !email) {
          feld.reportValidity();
          return;
        }
        senden.disabled = true;
        try {
          const antwort = await fetch(mailUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ r: code, email, handle, showroom, markt: KONFIG.markt ?? null, website: form.elements.website.value }),
          });
          if (!antwort.ok) throw new Error(`HTTP ${antwort.status}`);
          meldung.textContent = `Wir haben Ihr Ergebnis an ${email} geschickt.`;
          feld.value = '';
          form.hidden = true;
          oeffnen.hidden = false;
          oeffnen.setAttribute('aria-expanded', 'false');
        } catch (err) {
          console.warn(`[${rezept.name}] Ergebnis-Mail`, err);
          meldung.textContent = 'Die E-Mail konnte leider nicht verschickt werden. Bitte versuchen Sie es noch einmal.';
          meldung.classList.add('mf-kiosk-mail__meldung--fehler');
        } finally {
          senden.disabled = false;
          meldung.hidden = false;
        }
      });
    }

    /**
     * „In den Warenkorb" legt das Produkt ins Schubfach des Themes und fährt es auf.
     * Die eigentliche Arbeit macht das Modul in der Sektion (`inDenWarenkorb`), weil nur ein
     * Modul die Ereignisklasse des Themes laden kann.
     *
     * Fällt das Modul aus — kein Schubfach im Theme, Importfehler —, bleibt der alte Weg:
     * ablegen und zur Warenkorbseite wechseln. Lieber ein Seitenwechsel als ein Knopf,
     * der nichts tut.
     */
    function warenkorbVerdrahten() {
      container.querySelectorAll('.amq_mf_add_to_cart').forEach((knopf) => {
        if (!(knopf instanceof HTMLButtonElement)) return;
        knopf.addEventListener('click', async () => {
          const variante = Number(knopf.dataset.variant);
          if (!variante || knopf.disabled) return;
          rezept.beiWarenkorb?.(variante, hilfen);
          // Zwischen Klick und aufgefahrenem Schubfach liegt gut eine Sekunde — ohne Anzeige
          // sieht der Knopf in der Zeit aus, als hätte er nicht reagiert.
          knopf.disabled = true;
          knopf.classList.add('laedt');
          knopf.setAttribute('aria-busy', 'true');
          const fertig = () => {
            knopf.disabled = false;
            knopf.classList.remove('laedt');
            knopf.removeAttribute('aria-busy');
          };
          const inDenWarenkorb = window.amqFinder?.inDenWarenkorb ?? window.amqMatratzenfinder?.inDenWarenkorb;
          try {
            if (inDenWarenkorb) {
              await inDenWarenkorb(variante, 1, rezept.trackingSession?.());
              fertig();
              return;
            }
            await fetch(KONFIG.cartAddUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: variante, quantity: 1, properties: rezept.trackingSession?.() ? { _mf_session: rezept.trackingSession() } : {} }] }),
            });
            // Hier bleibt der Knopf im Ladezustand: Gleich wechselt die Seite.
            window.location.href = KONFIG.cartUrl;
          } catch (err) {
            console.error(`[${rezept.name}] Warenkorb`, err);
            fertig();
          }
        });
      });
    }

    /* ── Letztes Ergebnis ──────────────────────────────────────────────────── */

    function letztesErgebnisMerken(daten, fertig, code) {
      const eintrag = rezept.letztesErgebnis?.(daten, fertig, code, hilfen);
      if (!eintrag) return;
      try {
        localStorage.setItem(rezept.speicher, JSON.stringify({ ...eintrag, waehrung: KONFIG.geldWaehrung }));
      } catch { /* privater Modus: dann eben kein Banner */ }
    }

    function letztesErgebnisBanner() {
      let letztes;
      try {
        letztes = JSON.parse(localStorage.getItem(rezept.speicher) || 'null');
      } catch { return; }
      if (!letztes?.name) return;
      // Der Preis liegt als Betrag samt Währung im Browser und wird erst hier formatiert.
      // Ältere Einträge hatten nur einen fertigen Text in Euro — sie und Beträge aus einer
      // anderen Währung (Marktwechsel) zeigen das Banner ohne Preis.
      const preis =
        Number.isFinite(letztes.preisCent) && letztes.waehrung === KONFIG.geldWaehrung ? geld(letztes.preisCent) : '';
      // `zusatz` hieß in der Vorgängerfassung `haertegrad`; alte Einträge im Browser
      // sollen das Banner nicht mit „undefined" füllen.
      // Im Plugin steht das Banner auf dem Desktop als erstes Kind im Rumpf des ersten
      // Schritts — über der Überschrift, über die volle Breite des Kastens. Dadurch ist es
      // auch nur dort zu sehen: Ab Schritt 2 schiebt der Slider es mit aus dem Bild.
      const ziel = container.querySelector(rezept.bannerZiel);
      if (!ziel) return;
      const banner = document.createElement('div');
      banner.className = 'last-result-banner';
      banner.innerHTML = `
<a href="${esc(letztes.link)}">
  <div class="last-result-content">
    <div class="last-result-image">${letztes.bild ? `<img src="${esc(letztes.bild)}" alt="" width="80" height="60">` : ''}</div>
    <div class="last-result-info">
      <p class="last-result-name">${esc(letztes.name)}</p>
      <p class="last-result-config">${esc(letztes.masse)}, ${esc(letztes.zusatz ?? letztes.haertegrad ?? '')}${preis ? ` | <span class="last-result-price">${esc(preis)}</span>` : ''}</p>
    </div>
    <div class="last-result-actions">
      <span class="button-primary">Direkt zu Ihrem letzten Ergebnis
        <svg style="fill:white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z"/></svg>
      </span>
    </div>
  </div>
</a>`;
      ziel.prepend(banner);
    }

    /* ── Start ─────────────────────────────────────────────────────────────── */

    delegationVerdrahten();
    quizAufbauen();
    umschalterVerdrahten();

    // Ein Link mit ?r=… springt direkt zum Ergebnis, ohne das Quiz zu durchlaufen.
    const geteilt = new URLSearchParams(window.location.search).get('r');
    if (geteilt) ergebnisHolen(geteilt);
    else rezept.beiStart?.(hilfen);
  }

  window.AmqFinder = { starten };
})();
